'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import PackageCard from './PackageCard';
import CartDrawer from './CartDrawer';

interface TebexPackage {
  id: number;
  name: string;
  description: string;
  image: string | null;
  type: string;
  category: { id: number; name: string };
  base_price: number;
  sales_tax: number;
  total_price: number;
  currency: string;
  discount: number;
  disable_quantity: boolean;
}

interface TebexCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  packages: TebexPackage[];
  order: number;
}

interface BasketPackage {
  id: number;
  name: string;
  qty: number;
  base_price: number;
  total_price: number;
  currency: string;
}

interface Basket {
  ident: string;
  base_price: number;
  sales_tax: number;
  total_price: number;
  currency: string;
  packages: BasketPackage[];
  coupons: { coupon_code: string }[];
  creator_code: string;
  links: { checkout: string };
}

const SITE_URL = 'https://foundation8211.builtwithrocket.new';

function SkeletonCard() {
  return (
    <div
      className="rounded-sm overflow-hidden"
      style={{ background: 'var(--bg-card)', border: '1px solid rgba(201,168,76,0.08)' }}
    >
      <div className="h-44 animate-pulse" style={{ background: 'rgba(201,168,76,0.05)' }} />
      <div className="p-5 space-y-3">
        <div className="h-4 rounded animate-pulse" style={{ background: 'rgba(201,168,76,0.06)', width: '70%' }} />
        <div className="h-3 rounded animate-pulse" style={{ background: 'rgba(201,168,76,0.04)', width: '90%' }} />
        <div className="h-3 rounded animate-pulse" style={{ background: 'rgba(201,168,76,0.04)', width: '60%' }} />
        <div className="flex justify-between items-center pt-3" style={{ borderTop: '1px solid rgba(201,168,76,0.08)' }}>
          <div className="h-5 w-16 rounded animate-pulse" style={{ background: 'rgba(201,168,76,0.08)' }} />
          <div className="h-8 w-24 rounded animate-pulse" style={{ background: 'rgba(201,168,76,0.06)' }} />
        </div>
      </div>
    </div>
  );
}

export default function RedMStore() {
  const [categories, setCategories] = useState<TebexCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [basket, setBasket] = useState<Basket | null>(null);
  const [basketIdent, setBasketIdent] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);

  const sectionRef = useRef<HTMLDivElement>(null);

  // Fetch categories + packages
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/tebex/categories');
        if (!res.ok) {
          const data = await res.json();
          if (res.status === 503) {
            setError('store_not_configured');
          } else {
            setError(data.error || 'Failed to load store');
          }
          return;
        }
        const data = await res.json();
        const cats: TebexCategory[] = data.data || [];
        setCategories(cats);
        if (cats.length > 0) setActiveCategory(null); // null = All
      } catch {
        setError('Failed to connect to store');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.05 }
    );
    sectionRef.current?.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [categories, activeCategory]);

  // Get or create basket
  const getOrCreateBasket = useCallback(async (): Promise<string> => {
    if (basketIdent) return basketIdent;
    const res = await fetch('/api/tebex/baskets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        complete_url: `${SITE_URL}/redm?order=complete`,
        cancel_url: `${SITE_URL}/redm?order=cancelled`,
        complete_auto_redirect: true,
      }),
    });
    if (!res.ok) throw new Error('Failed to create basket');
    const data = await res.json();
    const ident = data.data.ident;
    setBasketIdent(ident);
    setBasket(data.data);
    return ident;
  }, [basketIdent]);

  // Refresh basket state
  const refreshBasket = useCallback(async (ident: string) => {
    const res = await fetch(`/api/tebex/baskets/${ident}`);
    if (!res.ok) return;
    const data = await res.json();
    setBasket(data.data);
  }, []);

  // Add to cart
  const handleAddToCart = useCallback(
    async (pkg: TebexPackage) => {
      setCartLoading(true);
      try {
        const ident = await getOrCreateBasket();
        const res = await fetch(`/api/tebex/baskets/${ident}/packages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ package_id: pkg.id, quantity: 1 }),
        });
        if (!res.ok) throw new Error('Failed to add package');
        const data = await res.json();
        setBasket(data);
        setCartOpen(true);
      } finally {
        setCartLoading(false);
      }
    },
    [getOrCreateBasket]
  );

  // Remove from cart
  const handleRemovePackage = useCallback(
    async (packageId: number) => {
      if (!basketIdent) return;
      const res = await fetch(`/api/tebex/baskets/${basketIdent}/packages/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId }),
      });
      if (!res.ok) throw new Error('Failed to remove package');
      const data = await res.json();
      setBasket(data);
    },
    [basketIdent]
  );

  // Apply coupon
  const handleApplyCoupon = useCallback(
    async (code: string) => {
      if (!basketIdent) throw new Error('No basket');
      const res = await fetch(`/api/tebex/baskets/${basketIdent}/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: code }),
      });
      if (!res.ok) throw new Error('Invalid coupon');
      const data = await res.json();
      setBasket(data.data);
    },
    [basketIdent]
  );

  // Apply creator code
  const handleApplyCreatorCode = useCallback(
    async (code: string) => {
      if (!basketIdent) throw new Error('No basket');
      const res = await fetch(`/api/tebex/baskets/${basketIdent}/creator-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_code: code }),
      });
      if (!res.ok) throw new Error('Invalid creator code');
      const data = await res.json();
      setBasket(data.data);
    },
    [basketIdent]
  );

  // Checkout
  const handleCheckout = useCallback(async () => {
    if (!basket?.links?.checkout) return;
    window.location.href = basket.links.checkout;
  }, [basket]);

  // Filtered packages
  const allPackages = categories.flatMap((c) => c.packages || []);
  const uniquePackages = allPackages.filter(
    (pkg, idx, self) => self.findIndex((p) => p.id === pkg.id) === idx
  );
  const displayPackages =
    activeCategory === null
      ? uniquePackages
      : categories.find((c) => c.id === activeCategory)?.packages || [];

  const cartItemCount = basket?.packages?.reduce((sum, p) => sum + (p.qty || 1), 0) ?? 0;
  const cartPackageIds = new Set(basket?.packages?.map((p) => p.id) ?? []);

  // Order status from URL
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const order = params.get('order');
      if (order) setOrderStatus(order);
    }
  }, []);

  return (
    <div ref={sectionRef}>
      {/* Hero */}
      <section
        className="relative pt-32 pb-16 px-6 overflow-hidden"
        style={{ background: 'var(--bg-obsidian)' }}
      >
        {/* Ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(201,168,76,0.05) 0%, transparent 70%)',
          }}
        />
        {/* Corner ornaments */}
        <div className="absolute top-8 left-8 w-10 h-10 opacity-20" style={{ borderTop: '1px solid var(--gold-primary)', borderLeft: '1px solid var(--gold-primary)' }} />
        <div className="absolute top-8 right-8 w-10 h-10 opacity-20" style={{ borderTop: '1px solid var(--gold-primary)', borderRight: '1px solid var(--gold-primary)' }} />

        <div className="max-w-7xl mx-auto text-center">
          <p
            className="font-mono text-[0.6rem] tracking-[0.5em] uppercase mb-4 reveal-on-scroll"
            style={{ color: 'var(--gold-dim)' }}
          >
            Foundation 1899 · RedM Store
          </p>
          <h1
            className="font-display text-5xl md:text-6xl lg:text-7xl font-700 mb-5 reveal-on-scroll"
            style={{ color: 'var(--text-primary)', lineHeight: 1.05 }}
          >
            The Script
            <br />
            <span className="text-gold-gradient">Emporium</span>
          </h1>
          <div className="divider-gold max-w-xs mx-auto my-6 reveal-on-scroll">
            <span className="font-mono text-[0.55rem] tracking-[0.3em] uppercase" style={{ color: 'var(--gold-dim)' }}>✦</span>
          </div>
          <p
            className="font-sans text-base max-w-xl mx-auto reveal-on-scroll"
            style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}
          >
            Premium RedM scripts crafted for immersive roleplay. Browse, add to cart, and
            checkout securely through Tebex.
          </p>
        </div>
      </section>

      {/* Order status banner */}
      {orderStatus && (
        <div
          className="px-6 py-4"
          style={{
            background:
              orderStatus === 'complete'
                ? 'rgba(110,175,122,0.1)' :'rgba(196,122,90,0.1)',
            borderTop: `1px solid ${orderStatus === 'complete' ? 'rgba(110,175,122,0.3)' : 'rgba(196,122,90,0.3)'}`,
            borderBottom: `1px solid ${orderStatus === 'complete' ? 'rgba(110,175,122,0.3)' : 'rgba(196,122,90,0.3)'}`,
          }}
        >
          <p
            className="font-mono text-xs tracking-[0.2em] uppercase text-center"
            style={{ color: orderStatus === 'complete' ? '#6EAF7A' : '#C47A5A' }}
          >
            {orderStatus === 'complete'
              ? '✓ Order complete — thank you for your purchase!' :'✗ Checkout cancelled — your cart has been preserved.'}
          </p>
        </div>
      )}

      {/* Main store content */}
      <section
        className="py-12 px-6"
        style={{ background: 'var(--bg-dark)' }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Error: not configured */}
          {error === 'store_not_configured' && (
            <div
              className="max-w-xl mx-auto text-center py-20 px-8 rounded-sm"
              style={{
                background: 'rgba(201,168,76,0.04)',
                border: '1px solid rgba(201,168,76,0.15)',
              }}
            >
              <div className="mb-6 opacity-40">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="mx-auto">
                  <circle cx="24" cy="24" r="23" stroke="#C9A84C" strokeWidth="1" />
                  <path d="M24 14v14M24 34v2" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h2 className="font-display text-2xl font-700 mb-3" style={{ color: 'var(--text-primary)' }}>
                Store Not Configured
              </h2>
              <p className="font-sans text-sm leading-relaxed mb-6" style={{ color: 'var(--text-muted)' }}>
                The Tebex store token hasn&apos;t been set up yet. Add your{' '}
                <code
                  className="font-mono text-xs px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold-primary)' }}
                >
                  TEBEX_TOKEN
                </code>{' '}
                environment variable to connect your store.
              </p>
              <div
                className="font-mono text-xs px-4 py-3 rounded-sm text-left"
                style={{
                  background: 'rgba(13,11,8,0.8)',
                  border: '1px solid rgba(201,168,76,0.15)',
                  color: 'var(--gold-dim)',
                }}
              >
                TEBEX_TOKEN=t66x-your-token-here
              </div>
            </div>
          )}

          {/* Generic error */}
          {error && error !== 'store_not_configured' && (
            <div className="text-center py-20">
              <p className="font-display text-xl" style={{ color: 'var(--text-muted)' }}>
                Unable to load store
              </p>
              <p className="font-mono text-xs tracking-widest uppercase mt-2" style={{ color: 'var(--text-dim)' }}>
                {error}
              </p>
            </div>
          )}

          {/* Loading skeletons */}
          {loading && !error && (
            <>
              <div className="flex gap-3 justify-center mb-12">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-8 w-24 rounded-sm animate-pulse"
                    style={{ background: 'rgba(201,168,76,0.06)' }}
                  />
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </>
          )}

          {/* Store content */}
          {!loading && !error && (
            <>
              {/* Category tabs */}
              <div className="flex flex-wrap gap-3 justify-center mb-12 reveal-on-scroll">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`filter-tab ${
                    activeCategory === null ? 'active' : ''
                  }`}
                >
                  All Scripts
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`filter-tab ${
                      activeCategory === cat.id ? 'active' : ''
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Count row */}
              <div className="flex items-center justify-between mb-8 reveal-on-scroll">
                <p
                  className="font-mono text-[0.6rem] tracking-[0.3em] uppercase"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {displayPackages.length} Script{displayPackages.length !== 1 ? 's' : ''}
                  {activeCategory !== null
                    ? ` · ${categories.find((c) => c.id === activeCategory)?.name}`
                    : ' · All Categories'}
                </p>
                <span
                  className="font-mono text-[0.55rem] tracking-[0.2em] uppercase"
                  style={{ color: 'var(--text-dim)' }}
                >
                  Lifetime updates included
                </span>
              </div>

              {/* Package grid */}
              {displayPackages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {displayPackages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      onAddToCart={handleAddToCart}
                      inCart={cartPackageIds.has(pkg.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-24">
                  <p className="font-display text-xl" style={{ color: 'var(--text-muted)' }}>
                    No scripts in this category yet.
                  </p>
                  <p
                    className="font-mono text-xs tracking-widest uppercase mt-2"
                    style={{ color: 'var(--text-dim)' }}
                  >
                    Check back soon — we ship updates regularly.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Floating cart button */}
      {!loading && !error && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-8 right-8 z-30 flex items-center gap-3 px-5 py-3.5 transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, #1A1410 0%, #221B12 100%)',
            border: '1px solid rgba(201,168,76,0.4)',
            borderRadius: '2px',
            boxShadow: cartItemCount > 0
              ? '0 0 30px rgba(201,168,76,0.2), 0 8px 32px rgba(0,0,0,0.5)'
              : '0 4px 24px rgba(0,0,0,0.4)',
          }}
          aria-label="Open cart"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="2" y="5" width="14" height="11" rx="1" stroke="#C9A84C" strokeWidth="1.2" />
            <path d="M6 5V4a3 3 0 016 0v1" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <span className="font-mono text-[0.65rem] tracking-[0.15em] uppercase" style={{ color: 'var(--gold-primary)' }}>
            Cart
          </span>
          {cartItemCount > 0 && (
            <span
              className="flex items-center justify-center w-5 h-5 font-mono text-[0.6rem] font-600 rounded-full"
              style={{ background: 'var(--gold-primary)', color: '#0D0B08' }}
            >
              {cartItemCount}
            </span>
          )}
        </button>
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        basket={basket}
        onRemovePackage={handleRemovePackage}
        onApplyCoupon={handleApplyCoupon}
        onApplyCreatorCode={handleApplyCreatorCode}
        onCheckout={handleCheckout}
        isLoading={cartLoading}
      />
    </div>
  );
}
